import { ReactElement, useState, useEffect, useCallback } from 'react';
import DeathMessage from '../DeathMessage';
import LeaveMessage from '../LeaveMessage';
import styles from '../ActiveScenarioSession.module.css';
import type { ScenarioMessage as ScenarioMessageType, TimelineEvent } from '../../../api/types/scenario';

/**
 * Type for timeline events with character data
 */
export interface TimelineEventWithCharacter {
  event_type: 'death' | 'leave';
  member_type?: 'character' | 'persona';
  character?: {
    id: string;
    username: string;
    display_name: string;
    icon: string;
  } | null;
  persona?: {
    id: string;
    display_name: string;
    icon: string;
  } | null;
}

/**
 * Hook to handle character death events specifically
 * @param messages Array of scenario messages to check for character death events
 * @returns Object containing state and handlers for character death
 */
export function useCharacterDeathHandler(messages: ScenarioMessageType[] = []) {
  const [characterDeathData, setCharacterDeathData] = useState<{
    id: string;
    display_name: string;
    icon: string;
    messageId: string;
  } | null>(null);

  // Check messages for character death events
  useEffect(() => {
    // Reset state when messages change or on initial mount
    setCharacterDeathData(null);

    if (!messages || messages.length === 0) {
       return; // Early return if no messages
    }

    // Get the latest message
    const latestMessage = messages[messages.length - 1];
    
    // Check if this message has any character death events
    if (latestMessage?.timeline_events && latestMessage.timeline_events.length > 0) {
      const characterDeathEvent = latestMessage.timeline_events.find(event => 
        event.event_type === 'death' && 
        event.member_type === 'character' &&
        event.character
      );

      if (characterDeathEvent && characterDeathEvent.character) {
        console.log('[DEBUG] Character death detected!', characterDeathEvent);
        setCharacterDeathData({
          id: characterDeathEvent.character.id,
          display_name: characterDeathEvent.character.display_name,
          icon: characterDeathEvent.character.icon,
          messageId: latestMessage.id
        });
      }
    }
  }, [messages]); // Only depend on messages

  // Reset handler for testing or scenario changes
  const resetCharacterDeathState = useCallback(() => {
    setCharacterDeathData(null);
  }, []);

  return {
    characterDeathData,
    resetCharacterDeathState,
    hasCharacterDied: !!characterDeathData
  };
}

/**
 * Hook to handle persona death events specifically
 * @param messages Array of scenario messages to check for persona death events
 * @returns Object containing state and handlers for persona death
 */
export function usePersonaDeathHandler(messages: ScenarioMessageType[] = []) {
  const [hasPersonaDied, setHasPersonaDied] = useState<boolean>(false);
  const [deathMessageId, setDeathMessageId] = useState<string | null>(null);
  const [personaDeathData, setPersonaDeathData] = useState<{
    id: string;
    display_name: string;
    icon: string;
  } | null>(null);

  // Check messages for persona death events
  useEffect(() => {
    // Reset state when messages change or on initial mount
    setHasPersonaDied(false);
    setDeathMessageId(null);
    setPersonaDeathData(null);

    if (!messages || messages.length === 0) {
       return; // Early return if no messages
    }

    // Get the latest message
    const latestMessage = messages[messages.length - 1];
    
    // Check if this message has any persona death events
    if (latestMessage?.timeline_events && latestMessage.timeline_events.length > 0) {
      const personaDeathEvent = latestMessage.timeline_events.find(event => 
        event.event_type === 'death' && 
        event.member_type === 'persona' &&
        event.persona
      );

      if (personaDeathEvent && personaDeathEvent.persona) {
        console.log('Persona death detected!', personaDeathEvent);
        setHasPersonaDied(true);
        setDeathMessageId(latestMessage.id);
        setPersonaDeathData({
          id: personaDeathEvent.persona.id,
          display_name: personaDeathEvent.persona.display_name,
          icon: personaDeathEvent.persona.icon
        });
      }
    }
  }, [messages]); // Only depend on messages

  // Reset handler for testing or scenario changes
  const resetPersonaDeathState = useCallback(() => {
    setHasPersonaDied(false);
    setDeathMessageId(null);
    setPersonaDeathData(null);
  }, []);

  return {
    hasPersonaDied,
    deathMessageId,
    personaDeathData,
    resetPersonaDeathState
  };
}

/**
 * Timeline events processing hook
 * @returns Object containing components and utilities for handling timeline events
 */
export function useTimelineEvents() {
  /**
   * Component to render death events for a specific message
   */
  const MessageDeathEvents = ({ 
    message, 
    isDevEnvironment = false,
    skipPersonaDeaths = false,
    skipCharacterDeaths = false
  }: {
    message: ScenarioMessageType;
    isDevEnvironment?: boolean;
    skipPersonaDeaths?: boolean;
    skipCharacterDeaths?: boolean;
  }): ReactElement | null => {
    // Only render if the message has timeline events with death events
    if (!message.timeline_events || message.timeline_events.length === 0) {
      return null;
    }

    console.log(`[DEBUG] Processing MessageDeathEvents for message ID: ${message.id}`);
    console.log(`[DEBUG] Message has ${message.timeline_events.length} timeline events`);
    console.log(`[DEBUG] skipPersonaDeaths: ${skipPersonaDeaths}, skipCharacterDeaths: ${skipCharacterDeaths}`);

    // Filter for valid death events with character data
    const characterDeathEvents = skipCharacterDeaths ? [] : message.timeline_events
      .filter(event => 
        event.event_type === 'death' && 
        event.character &&
        event.character.id &&
        event.character.display_name &&
        event.character.icon
      )
      .map(event => ({
        event_type: event.event_type,
        member_type: 'character' as const,
        character: {
          id: event.character!.id,
          username: event.character!.username || '',
          display_name: event.character!.display_name,
          icon: event.character!.icon || ''
        }
      }));

    console.log(`[DEBUG] Found ${characterDeathEvents.length} character death events`);
    if (characterDeathEvents.length > 0) {
      console.log('[DEBUG] Character death events:', characterDeathEvents);
    }

    // Filter for valid death events with persona data
    // Only include persona deaths if not skipping them
    const personaDeathEvents = skipPersonaDeaths ? [] : message.timeline_events
      .filter(event => 
        event.event_type === 'death' && 
        event.member_type === 'persona' &&
        event.persona &&
        event.persona.id &&
        event.persona.display_name &&
        event.persona.icon
      )
      .map(event => ({
        event_type: event.event_type,
        member_type: 'persona' as const,
        persona: {
          id: event.persona!.id,
          display_name: event.persona!.display_name,
          icon: event.persona!.icon
        }
      }));

    console.log(`[DEBUG] Found ${personaDeathEvents.length} persona death events`);
    if (personaDeathEvents.length > 0) {
      console.log('[DEBUG] Persona death events:', personaDeathEvents);
    }

    // Combine both types of death events
    const deathEvents = [...characterDeathEvents, ...personaDeathEvents];

    // If no death events, don't render anything
    if (deathEvents.length === 0) {
      console.log('[DEBUG] No death events to render for this message');
      return null;
    }

    console.log(`[DEBUG] Rendering ${deathEvents.length} death events for message ID: ${message.id}`);

    return (
      <div className={styles.timelineEventsContainer}>
        {deathEvents.map((event, index) => {
          // For persona deaths (user deaths), display "You have died"
          if (event.member_type === 'persona' && 'persona' in event) {
            console.log(`[DEBUG] Creating persona death instance: ${event.persona.id}, index: ${index}, messageId: ${message.id}`);
            return (
              <DeathMessage
                key={`death-persona-${event.persona.id}-${index}-${message.id}`}
                icon={event.persona.icon}
                displayName="You"
                isPersona={true}
              />
            );
          } 
          // For character deaths
          else if ('character' in event && event.character) {
            console.log(`[DEBUG] Creating character death instance: ${event.character.display_name} (${event.character.id}), index: ${index}, messageId: ${message.id}`);
            return (
              <DeathMessage
                key={`death-${event.character.id}-${index}-${message.id}`}
                icon={event.character.icon}
                displayName={event.character.display_name}
                isPersona={false}
              />
            );
          }
          return null;
        })}
      </div>
    );
  };

  /**
   * Component to render leave events for a specific message
   */
  const MessageLeaveEvents = ({ 
    message,
    isDevEnvironment = false 
  }: {
    message: ScenarioMessageType;
    isDevEnvironment?: boolean;
  }): ReactElement | null => {
    // Only render if the message has timeline events with leave events
    if (!message.timeline_events || message.timeline_events.length === 0) {
      return null;
    }

    // Filter for valid leave events with character data
    const leaveEvents = message.timeline_events
      .filter(event => 
        event.event_type === 'leave' && 
        event.character &&
        event.character.id &&
        event.character.display_name &&
        event.character.icon
      )
      .map(event => ({
        event_type: event.event_type,
        character: {
          id: event.character!.id,
          username: event.character!.username || '',
          display_name: event.character!.display_name,
          icon: event.character!.icon || ''
        }
      }));

    // If no leave events, don't render anything
    if (leaveEvents.length === 0) {
      return null;
    }

    console.log(`[DEBUG] Rendering ${leaveEvents.length} leave events for message ID: ${message.id}`);

    return (
      <div className={styles.timelineEventsContainer}>
        {leaveEvents.map((event, index) => (
          <LeaveMessage
            key={`leave-${event.character.id}-${index}-${message.id}`}
            icon={event.character.icon}
            displayName={event.character.display_name}
          />
        ))}
      </div>
    );
  };

  return {
    MessageDeathEvents,
    MessageLeaveEvents
  };
}

export default useTimelineEvents;
